import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { asDisplayText } from './walletPage.utils';

export function StatusPill({
  value,
  asIs = false,
}: {
  value?: unknown;
  /** When true, show API status text unchanged (transactions). */
  asIs?: boolean;
}) {
  const raw = asDisplayText(value);
  const upper = raw.toUpperCase().replace(/[\s-]+/g, '_');
  const tone = resolveStatusTone(upper);

  const cls =
    tone === 'success'
      ? 'text-emerald-700 bg-emerald-50'
      : tone === 'pending'
        ? 'text-amber-700 bg-amber-50'
        : tone === 'error'
          ? 'text-red-700 bg-red-50'
          : 'text-[var(--dash-muted)] bg-[var(--dash-soft)]';

  const label = asIs
    ? raw || '—'
    : upper === 'SUCCESS'
      ? 'Completed'
      : raw
        ? upper.charAt(0) + upper.slice(1).toLowerCase()
        : '—';

  return (
    <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
      {label}
    </span>
  );
}

function resolveStatusTone(
  upper: string,
): 'success' | 'pending' | 'error' | 'neutral' {
  if (!upper) return 'neutral';
  if (
    /SUCCESS|COMPLETED|COMPLETE|PAID|APPROVED|CREDITED|CREDIT|ACTIVE|DONE|SETTLED/.test(
      upper,
    )
  ) {
    return 'success';
  }
  if (
    /PENDING|PROCESSING|INITIATED|IN_PROGRESS|QUEUED|WAITING|ONGOING/.test(
      upper,
    )
  ) {
    return 'pending';
  }
  if (
    /FAIL|REJECT|CANCEL|DECLINE|ERROR|EXPIRED|REVERSED|DEBITED/.test(upper)
  ) {
    return 'error';
  }
  return 'neutral';
}

export function Th({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={`px-4 py-2.5 text-[11px] font-semibold tracking-wider text-[var(--dash-muted)] uppercase ${className}`}
    >
      {children}
    </th>
  );
}

export function Pager({
  page,
  totalPages,
  total,
  pageSize,
  isFetching = false,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  isFetching?: boolean;
  onChange: (p: number) => void;
}) {
  if (total <= 0) return null;

  const rangeStart = (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);
  const pageNumbers = buildVisiblePages(page, totalPages, 5);

  return (
    <div className="flex flex-col gap-3 border-t border-[var(--dash-line)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="m-0 text-left text-xs text-[var(--dash-muted)] sm:text-sm">
        Showing{' '}
        <span className="font-semibold text-[var(--dash-ink)]">
          {rangeStart}–{rangeEnd}
        </span>{' '}
        of <span className="font-semibold text-[var(--dash-ink)]">{total}</span>
        {isFetching ? (
          <span className="ml-2 text-[var(--dash-brand)]">Updating…</span>
        ) : null}
      </p>

      {totalPages > 1 ? (
        <nav
          className="flex flex-wrap items-center gap-1"
          aria-label="Pagination"
        >
          <button
            type="button"
            disabled={page <= 1 || isFetching}
            onClick={() => onChange(page - 1)}
            className="inline-flex h-8 items-center gap-1 rounded-lg border border-[var(--dash-line)] bg-white px-2.5 text-xs font-semibold text-[var(--dash-ink)] transition hover:bg-[var(--dash-soft)] disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Prev
          </button>

          {pageNumbers.map((p) => (
            <button
              key={p}
              type="button"
              disabled={isFetching}
              onClick={() => onChange(p)}
              aria-current={p === page ? 'page' : undefined}
              className={`inline-flex h-8 min-w-8 items-center justify-center rounded-lg px-2 text-xs font-semibold transition disabled:opacity-50 ${
                p === page
                  ? 'bg-[var(--dash-brand)] text-white shadow-sm'
                  : 'border border-[var(--dash-line)] bg-white text-[var(--dash-ink)] hover:bg-[var(--dash-soft)]'
              }`}
            >
              {p}
            </button>
          ))}

          <button
            type="button"
            disabled={page >= totalPages || isFetching}
            onClick={() => onChange(page + 1)}
            className="inline-flex h-8 items-center gap-1 rounded-lg border border-[var(--dash-line)] bg-white px-2.5 text-xs font-semibold text-[var(--dash-ink)] transition hover:bg-[var(--dash-soft)] disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Next page"
          >
            Next
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </nav>
      ) : null}
    </div>
  );
}

/** Sliding window of up to `windowSize` page numbers around the current page. */
function buildVisiblePages(
  current: number,
  total: number,
  windowSize = 5,
): number[] {
  if (total <= windowSize) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const half = Math.floor(windowSize / 2);
  let start = Math.max(1, current - half);
  let end = start + windowSize - 1;

  if (end > total) {
    end = total;
    start = end - windowSize + 1;
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export function Spinner() {
  return (
    <div className="flex justify-center py-16">
      <Loader2 className="h-5 w-5 animate-spin text-[var(--dash-brand)]" />
    </div>
  );
}

export function EmptyMsg({ text }: { text: string }) {
  return (
    <p className="py-16 text-center text-sm text-[var(--dash-muted)]">{text}</p>
  );
}
